'use client';

import React from 'react';
import { twMerge } from 'tailwind-merge';

export type PulseTextAreaVariant = 'outlined' | 'soft' | 'plain';
export type PulseTextAreaSize = 'sm' | 'md' | 'lg';
export type PulseTextAreaResize = 'none' | 'both' | 'horizontal' | 'vertical';

export interface PulseTextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  variant?: PulseTextAreaVariant;
  size?: PulseTextAreaSize;
  error?: boolean;
  fullWidth?: boolean;
  resize?: PulseTextAreaResize;
  minRows?: number;
  maxRows?: number;
  sx?: React.CSSProperties;
  slotProps?: {
    root?: { className?: string };
    textarea?: { className?: string };
  };
}

const getContainerClasses = (
  variant: PulseTextAreaVariant,
  size: PulseTextAreaSize,
  error: boolean,
  fullWidth: boolean,
  disabled: boolean | undefined,
  focused: boolean
) => {
  const baseClasses =
    'flex items-stretch rounded-lg transition-all duration-200 overflow-hidden';
  const widthClasses = fullWidth ? 'w-full' : 'w-auto';

  const variantClasses = {
    outlined: [
      'border border-neutral-200',
      focused ? 'border-primary-500' : 'hover:border-neutral-500',
      error ? '!border-red-500 hover:!border-red-600' : '',
      disabled ? 'border-neutral-100' : '',
    ].join(' '),
    soft: [
      'bg-neutral-100/50',
      focused ? 'bg-neutral-100' : 'hover:bg-neutral-100',
      error ? 'bg-red-50 hover:bg-red-100' : '',
      disabled ? 'bg-neutral-50' : '',
    ].join(' '),
    plain: [
      focused ? 'bg-neutral-100/50' : 'hover:bg-neutral-50',
      error ? 'bg-red-50 hover:bg-red-100' : '',
      disabled ? 'bg-transparent' : '',
    ].join(' '),
  };

  const sizeClasses = {
    sm: 'min-h-20 text-xs',
    md: 'min-h-24 text-sm',
    lg: 'min-h-32 text-sm',
  };

  return twMerge(
    baseClasses,
    widthClasses,
    variantClasses[variant],
    sizeClasses[size]
  );
};

const getPaddingClasses = (size: PulseTextAreaSize) => {
  const paddingMap = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
  };

  return paddingMap[size];
};

const getTextareaClasses = (
  disabled: boolean | undefined,
  resize: PulseTextAreaResize
) => {
  const resizeMap = {
    none: 'resize-none',
    both: 'resize',
    horizontal: 'resize-x',
    vertical: 'resize-y',
  };

  return twMerge(
    'flex-1 min-w-0 w-full bg-transparent outline-none',
    'placeholder:text-neutral-500',
    disabled ? 'cursor-not-allowed text-neutral-300' : 'text-neutral-900',
    resizeMap[resize]
  );
};

const PulseTextArea = React.forwardRef<
  HTMLTextAreaElement,
  PulseTextAreaProps
>(
  (
    {
      variant = 'outlined',
      size = 'md',
      error = false,
      fullWidth = true,
      resize = 'vertical',
      disabled,
      className = '',
      sx = {},
      slotProps = {},
      minRows,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = React.useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      props.onBlur?.(e);
    };

    return (
      <div
        className={twMerge(
          getContainerClasses(
            variant,
            size,
            error,
            fullWidth,
            disabled,
            focused
          ),
          className,
          slotProps.root?.className
        )}
        style={sx}
      >
        <textarea
          ref={ref}
          disabled={disabled}
          rows={minRows}
          className={twMerge(
            getTextareaClasses(disabled, resize),
            getPaddingClasses(size),
            slotProps.textarea?.className
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
        />
      </div>
    );
  }
);

PulseTextArea.displayName = 'PulseTextArea';

export default PulseTextArea;
